import { Layout } from '@/components/layout';
import { fmtDate } from '@/lib/dateFormat';
import { useAuthHeaders } from '@/lib/auth-store';
import { useGetMe } from '@workspace/api-client-react';
import { useEffect, useRef, useState } from 'react';
import { CreditCard, CheckCircle2, AlertCircle, Clock, ArrowRight, Loader2, ExternalLink, PartyPopper } from 'lucide-react';
import { cn } from '@/lib/utils';

const API_BASE = import.meta.env.BASE_URL.replace(/\/$/, '');

interface BillingInfo {
  planName: string | null;
  planPriceCents: number | null;
  stripeCustomerId: string | null;
  subscription: {
    id: string;
    status: string;
    current_period_end: string | number;
    cancel_at_period_end: boolean;
  } | null;
}

function formatAUD(cents: number) {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(cents / 100);
}

function formatDate(val: string | number) {
  return fmtDate(val);
}

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string; icon: typeof CheckCircle2 }> = {
  active:   { label: 'Active',    color: 'text-emerald-400', bg: 'bg-emerald-400/10 border-emerald-400/20', icon: CheckCircle2 },
  trialing: { label: 'Trialing',  color: 'text-sky-400',     bg: 'bg-sky-400/10 border-sky-400/20',         icon: Clock },
  past_due: { label: 'Past Due',  color: 'text-amber-400',   bg: 'bg-amber-400/10 border-amber-400/20',     icon: AlertCircle },
  canceled: { label: 'Canceled',  color: 'text-muted-foreground',   bg: 'bg-slate-400/10 border-slate-400/20',     icon: AlertCircle },
  unpaid:   { label: 'Unpaid',    color: 'text-rose-400',    bg: 'bg-rose-400/10 border-rose-400/20',       icon: AlertCircle },
};

export function Billing() {
  const authOpts = useAuthHeaders();
  const { data: me } = useGetMe({ request: authOpts });

  const [billing, setBilling] = useState<BillingInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const token = (authOpts as any)?.headers?.Authorization?.replace('Bearer ', '');

  // Detect Stripe redirect result
  const params = new URLSearchParams(window.location.search);
  const justSubscribed = params.get('billing_success') === '1';
  const justCanceled = params.get('billing_canceled') === '1';

  // After a successful payment Stripe's webhook may take a few seconds to arrive.
  // Poll until the subscription appears as active (up to ~10 s).
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollCount = useRef(0);

  const fetchBilling = async (): Promise<BillingInfo | null> => {
    if (!token) return null;
    try {
      const res = await fetch(`${API_BASE}/api/billing/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data: BillingInfo = await res.json();
        setBilling(data);
        return data;
      }
    } catch {}
    return null;
  };

  // Initial load
  useEffect(() => {
    if (!token) return;
    setLoading(true);
    fetchBilling().finally(() => setLoading(false));
  }, [token]);

  // If we just came back from a successful Stripe payment, poll until the
  // subscription shows up (webhook latency) — max 5 attempts × 2 s = 10 s.
  useEffect(() => {
    if (!justSubscribed || !token) return;

    pollRef.current = setInterval(async () => {
      pollCount.current += 1;
      const data = await fetchBilling();
      const isActive = data?.subscription?.status === 'active' || data?.subscription?.status === 'trialing';
      if (isActive || pollCount.current >= 5) {
        clearInterval(pollRef.current!);
      }
    }, 2000);

    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [justSubscribed, token]);

  const handleSubscribe = async () => {
    if (!token) return;
    setActionLoading(true);
    setErrorMsg(null);
    const win = window.open('', '_blank');
    try {
      const returnUrl = window.location.origin + import.meta.env.BASE_URL + 'settings/billing';
      const res = await fetch(`${API_BASE}/api/billing/checkout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ returnUrl }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to start checkout');
      if (win) win.location.href = data.url;
    } catch (err: any) {
      win?.close();
      setErrorMsg(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handlePortal = async () => {
    if (!token) return;
    setActionLoading(true);
    setErrorMsg(null);
    const win = window.open('', '_blank');
    try {
      const returnUrl = window.location.origin + import.meta.env.BASE_URL + 'settings/billing';
      const res = await fetch(`${API_BASE}/api/billing/portal`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ returnUrl }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to open portal');
      if (win) win.location.href = data.url;
    } catch (err: any) {
      win?.close();
      setErrorMsg(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const sub = billing?.subscription;
  const statusCfg = sub ? (STATUS_CONFIG[sub.status] ?? STATUS_CONFIG['canceled']!) : null;
  const isActive = sub?.status === 'active' || sub?.status === 'trialing';
  const hasPlan = !!(billing?.planName && billing?.planPriceCents);

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-foreground">Billing &amp; Subscription</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage your SoraFlo subscription and billing details.</p>
        </div>

        {/* ── Thank-you banner ── */}
        {justSubscribed && (
          <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-6 flex flex-col items-center text-center gap-3">
            <div className="w-12 h-12 rounded-full bg-emerald-400/15 border border-emerald-400/30 flex items-center justify-center">
              <PartyPopper className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <p className="text-lg font-bold text-emerald-600 dark:text-emerald-300">You're subscribed!</p>
              <p className="text-sm text-emerald-700/80 dark:text-emerald-400/80 mt-1">
                {hasPlan
                  ? `Your ${billing!.planName} plan is now active. We've sent a confirmation to your email.`
                  : isActive
                    ? 'Your subscription is now active. Thanks for subscribing!'
                    : 'Payment received — your subscription is being activated. This usually takes a few seconds.'}
              </p>
            </div>
            {!isActive && (
              <div className="flex items-center gap-2 text-xs text-emerald-700/60 dark:text-emerald-400/60">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Confirming with Stripe…
              </div>
            )}
          </div>
        )}

        {/* Canceled notice */}
        {justCanceled && !justSubscribed && (
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-rose-500/20 bg-rose-500/10 text-sm text-rose-600 dark:text-rose-300">
            <AlertCircle className="w-4 h-4 shrink-0" />
            Checkout was canceled — no charge was made.
          </div>
        )}

        {/* Error */}
        {errorMsg && (
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-rose-500/20 bg-rose-500/10 text-sm text-rose-600 dark:text-rose-300">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {errorMsg}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 text-muted-foreground animate-spin" />
          </div>
        ) : (
          <>
            {/* Plan card */}
            <div className="rounded-2xl border border-border bg-card p-6 space-y-5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Current Plan</p>
                    <p className="text-lg font-bold text-foreground mt-0.5">
                      {hasPlan ? billing!.planName! : 'No plan assigned'}
                    </p>
                  </div>
                </div>

                {/* Status badge */}
                {statusCfg && (
                  <div className={cn(
                    "flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold",
                    statusCfg.bg, statusCfg.color
                  )}>
                    <statusCfg.icon className="w-3.5 h-3.5" />
                    {statusCfg.label}
                    {sub?.cancel_at_period_end && ' · Canceling'}
                  </div>
                )}
              </div>

              {/* Price */}
              {hasPlan && (
                <div className="flex items-baseline gap-1.5">
                  <span className="text-3xl font-bold text-foreground">{formatAUD(billing!.planPriceCents!)}</span>
                  <span className="text-sm text-muted-foreground">AUD / month</span>
                </div>
              )}

              {/* Subscription details */}
              {sub && (
                <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border/50">
                  <div className="rounded-xl bg-muted/50 px-4 py-3">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                      {sub.cancel_at_period_end ? 'Cancels On' : 'Next Billing'}
                    </p>
                    <p className="text-sm font-semibold text-foreground mt-1">
                      {formatDate(sub.current_period_end)}
                    </p>
                  </div>
                  <div className="rounded-xl bg-muted/50 px-4 py-3">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Billing Cycle</p>
                    <p className="text-sm font-semibold text-foreground mt-1">Monthly</p>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                {isActive ? (
                  <button
                    onClick={handlePortal}
                    disabled={actionLoading}
                    className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-[#0A0B0D] text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 shadow-[0_0_16px_rgba(47,230,168,0.25)]"
                  >
                    {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
                    {actionLoading ? 'Redirecting…' : 'Manage Subscription'}
                  </button>
                ) : hasPlan ? (
                  <button
                    onClick={handleSubscribe}
                    disabled={actionLoading}
                    className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-[#0A0B0D] text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 shadow-[0_0_16px_rgba(47,230,168,0.25)]"
                  >
                    {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                    {actionLoading ? 'Redirecting…' : 'Subscribe Now'}
                  </button>
                ) : null}
              </div>

              {/* No plan message */}
              {!hasPlan && (
                <p className="text-sm text-muted-foreground pt-1">
                  Your account hasn't been assigned a plan yet. Please contact{' '}
                  <a href="mailto:support@soraflo.io" className="text-primary hover:underline">support@soraflo.io</a>{' '}
                  to get set up.
                </p>
              )}
            </div>

            {/* Info note */}
            <p className="text-xs text-muted-foreground text-center">
              Billing is powered by Stripe. Your payment details are never stored on our servers.
            </p>
          </>
        )}
      </div>
    </Layout>
  );
}
